import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface PricingBasisChipProps {
  basis: 'per_case' | 'per_unit' | 'both' | 'unknown';
  rawText?: string;
  className?: string;
}

export function PricingBasisChip({ basis, rawText, className }: PricingBasisChipProps) {
  const getVariant = () => {
    switch (basis) {
      case 'per_unit':
        return 'default' as const; // Blue
      case 'per_case':
        return 'secondary' as const; // Purple
      case 'both':
        return 'outline' as const; // Green
      case 'unknown':
      default:
        return 'destructive' as const; // Gray
    }
  };

  const getLabel = () => {
    switch (basis) {
      case 'per_unit':
        return 'Per Unit';
      case 'per_case':
        return 'Per Case';
      case 'both':
        return 'Both';
      case 'unknown':
      default:
        return 'Unknown';
    }
  };

  const getDescription = () => {
    switch (basis) {
      case 'per_unit':
        return 'Pricing is unit-based (EA, EACH, PC, etc.)';
      case 'per_case':
        return 'Pricing is case-based (CS, CTN, BOX, etc.)';
      case 'both':
        return 'Both unit and case pricing detected';
      case 'unknown':
      default:
        return 'Could not determine pricing basis';
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant={getVariant()} className={className}>
            {getLabel()}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <p className="font-medium">{getDescription()}</p>
            {rawText && (
              <p className="text-xs text-muted-foreground">
                Raw text: "{rawText}"
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}